import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import TicketTransferModal from '../components/TicketTransferModal';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface ShiftStatus {
  shiftStatus: 'offline' | 'online' | 'break' | 'busy';
  shiftStartTime: string | null;
  shiftEndTime: string | null;
  totalShiftTime: number;
  lastShiftStart: string | null;
  lastShiftEnd: string | null;
  shiftNotes: string;
}

const ShiftManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [shiftStatus, setShiftStatus] = useState<ShiftStatus>({
    shiftStatus: 'offline',
    shiftStartTime: null,
    shiftEndTime: null,
    totalShiftTime: 0,
    lastShiftStart: null,
    lastShiftEnd: null,
    shiftNotes: ''
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [openTickets, setOpenTickets] = useState<any[]>([]);

  useEffect(() => {
    fetchShiftStatus();
  }, []);

  const fetchShiftStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/shifts/status');
      
      if (response.data.success && response.data.data) {
        const apiData = response.data.data;
        let processedData = apiData;
        
        if (!apiData.shiftStatus && apiData.status) {
          processedData = {
            ...apiData,
            shiftStatus: apiData.status
          };
        }
        
        if (!processedData.shiftStatus) {
          processedData = {
            shiftStatus: 'offline',
            shiftStartTime: apiData.shiftStartTime || null,
            shiftEndTime: apiData.shiftEndTime || null,
            totalShiftTime: apiData.totalShiftTime || 0,
            lastShiftStart: apiData.lastShiftStart || null,
            lastShiftEnd: apiData.lastShiftEnd || null,
            shiftNotes: apiData.shiftNotes || ''
          };
        }
        
        setShiftStatus(processedData);
      } else {
        setShiftStatus({
          shiftStatus: 'offline',
          shiftStartTime: null,
          shiftEndTime: null,
          totalShiftTime: 0,
          lastShiftStart: null,
          lastShiftEnd: null,
          shiftNotes: ''
        });
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки статуса смены');
      setShiftStatus({
        shiftStatus: 'offline',
        shiftStartTime: null,
        shiftEndTime: null,
        totalShiftTime: 0,
        lastShiftStart: null,
        lastShiftEnd: null,
        shiftNotes: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartShift = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/api/shifts/start', { 
        notes: notes.trim() || 'Смена начата'
      });
      if (response.data.success) {
        setShiftStatus(response.data.data);
        setNotes('');
        toast.success('Смена начата успешно!');
        fetchShiftStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при начале смены');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTakeBreak = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/api/shifts/break');
      if (response.data.success) {
        setShiftStatus(response.data.data);
        toast.success('Перерыв начат!');
        fetchShiftStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при начале перерыва');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetBusy = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/api/shifts/busy');
      if (response.data.success) {
        setShiftStatus(response.data.data);
        toast.success('Статус изменен на "Занят"!');
        fetchShiftStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при изменении статуса');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeShift = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/api/shifts/resume');
      if (response.data.success) {
        setShiftStatus(response.data.data);
        toast.success('Смена возобновлена!');
        fetchShiftStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при возобновлении смены');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndShift = async () => {
    // Открытые заявки больше не блокируют завершение: бэкенд сам передаёт их
    // в общий пул следующей смены (в работе → пауза, оператор снимается).
    let openCount = 0;
    try {
      const openTicketsResponse = await api.get('/api/shifts/open-tickets');
      if (openTicketsResponse.data.success && openTicketsResponse.data.data.hasOpenTickets) {
        openCount = openTicketsResponse.data.data.count || (openTicketsResponse.data.data.tickets || []).length;
        setOpenTickets(openTicketsResponse.data.data.tickets || []);
      }
    } catch (error: any) {
      console.error('Error checking open tickets:', error);
    }

    const question = openCount
      ? `У вас ${openCount} открытых заявок. Они автоматически перейдут в общий список для следующей смены (в работе → пауза). Завершить смену?`
      : 'Вы уверены, что хотите завершить смену?';
    if (!window.confirm(question)) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post('/api/shifts/end');
      if (response.data.success) {
        setShiftStatus(response.data.data);
        const handed = response.data.data?.handedOver || 0;
        toast.success(handed ? `Смена завершена. Передано на смену заявок: ${handed}` : 'Смена завершена!');
        fetchShiftStatus();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Ошибка при завершении смены';
      toast.error(errorMessage);
      
      // Если ошибка из-за открытых заявок, показываем модальное окно
      if (error.response?.data?.data?.openTickets) {
        setOpenTickets(error.response.data.data.openTickets);
        setShowTransferModal(true);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferComplete = () => {
    // После передачи заявок пытаемся закрыть смену снова
    handleEndShift();
  };

  const formatDuration = (minutes: number) => {
    if (!minutes || isNaN(minutes)) return '0ч 0м';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}ч ${mins}м`;
  };

  const formatTime = (time: string | null) => {
    if (!time) return 'Не указано';
    try {
      return new Date(time).toLocaleString('ru-RU');
    } catch {
      return 'Не указано';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online':
        return <Badge variant="default" className="bg-success hover:bg-success/90">На смене</Badge>;
      case 'offline':
        return <Badge variant="outline">Не на смене</Badge>;
      case 'break':
        return <Badge variant="outline" className="bg-status-paused/10 text-status-paused border-status-paused/20">Перерыв</Badge>;
      case 'busy':
        return <Badge variant="destructive">Занят</Badge>;
      default:
        return <Badge variant="outline">Не на смене</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Загрузка статуса смены...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Управление сменой</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Оператор: {user?.firstName} {user?.lastName}
        </p>
      </div>

      {/* Текущий статус */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Текущий статус</h2>
          {getStatusBadge(shiftStatus.shiftStatus)}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <ClockIcon className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Общее время</p>
              <p className="text-lg font-semibold text-foreground">
                {formatDuration(shiftStatus.totalShiftTime)}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Начало смены</p>
            <p className="text-sm font-medium text-foreground">
              {formatTime(shiftStatus.shiftStartTime)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Последняя смена</p>
            <p className="text-sm font-medium text-foreground">
              {formatTime(shiftStatus.lastShiftStart)}
            </p>
          </div>
        </div>
        
        {shiftStatus.shiftNotes && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground mb-1">Заметки</p>
            <p className="text-sm text-foreground">{shiftStatus.shiftNotes}</p>
          </div>
        )}
      </div>

      {/* Действия */}
      <div className="bg-card border border-border rounded-lg p-6">
        {shiftStatus.shiftStatus === 'offline' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Выход на смену</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Заметки о смене (необязательно)"
              rows={3}
            />
            <Button
              onClick={handleStartShift}
              disabled={actionLoading}
              className="w-full bg-success hover:bg-success/90"
              size="lg"
            >
              <PlayIcon className="h-5 w-5 mr-2" />
              {actionLoading ? 'Начинаем...' : 'Начать смену'}
            </Button>
          </div>
        )}

        {(shiftStatus.shiftStatus === 'online' || shiftStatus.shiftStatus === 'break' || shiftStatus.shiftStatus === 'busy') && (
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-foreground">Действия на смене</h3>
            
            {shiftStatus.shiftStatus === 'online' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={handleTakeBreak}
                  disabled={actionLoading}
                  variant="outline"
                  className="w-full"
                >
                  <PauseIcon className="h-4 w-4 mr-2" />
                  Перерыв
                </Button>
                <Button
                  onClick={handleSetBusy}
                  disabled={actionLoading}
                  variant="destructive"
                  className="w-full"
                >
                  Занят
                </Button>
              </div>
            )}

            {(shiftStatus.shiftStatus === 'break' || shiftStatus.shiftStatus === 'busy') && (
              <Button
                onClick={handleResumeShift}
                disabled={actionLoading}
                className="w-full"
                size="lg"
              >
                <PlayIcon className="h-5 w-5 mr-2" />
                {shiftStatus.shiftStatus === 'break' ? 'Продолжить смену' : 'Свободен'}
              </Button>
            )}

            <Button
              onClick={handleEndShift}
              disabled={actionLoading}
              variant="outline"
              className="w-full"
            >
              <StopIcon className="h-4 w-4 mr-2" />
              Завершить смену
            </Button>
          </div>
        )}
      </div>

      {/* Информация */}
      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3 text-foreground">Информация о статусах</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
          <div>
            <Badge variant="default" className="bg-success hover:bg-success/90 mr-2">На смене</Badge>
            <span>— оператор готов принимать заявки</span>
          </div>
          <div>
            <Badge variant="destructive" className="mr-2">Занят</Badge>
            <span>— оператор работает с заявкой</span>
          </div>
          <div>
            <Badge variant="outline" className="bg-status-paused/10 text-status-paused border-status-paused/20 mr-2">Перерыв</Badge>
            <span>— оператор временно недоступен</span>
          </div>
          <div>
            <Badge variant="outline" className="mr-2">Не на смене</Badge>
            <span>— оператор не принимает заявки</span>
          </div>
        </div>
      </div>

      {/* Модальное окно передачи заявок */}
      <TicketTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        tickets={openTickets}
        onTransferComplete={handleTransferComplete}
      />
    </div>
  );
};

export default ShiftManagementPage;
